/**
 * Eagle tattoos — authored via the `/pack` toile process (plan
 * `thinking/plans/2026_08_31_toile_pack_wiring.md` Track B), wave `toile_tattoo_subjects`. Narcis file-split
 * shape (Track A): one module per pack, `registry.ts` assembles them into `livePacks`.
 */
import {tagline, chips, type ToilePack} from './data'

export const eagleTattoosPack: ToilePack = {
    slug: 'eagle-tattoos',
    category: 'tattoo',
    name: 'Eagle tattoos',
    tagline: tagline('pieces', 'themes', 'styles'),
    chips: chips('themes', 'styles'),
    ratio: {w: 1216, h: 832},
    readings: [
        {key: 'spread', label: 'Wings spread', subject: 'an eagle facing forward with both wings spread fully open across the page, talons gripping a branch'},
        {key: 'head', label: 'Eagle head', subject: "an eagle's head in profile, the hooked beak and the fierce eye, the neck feathers drawn one by one"},
        {key: 'diving', label: 'Diving eagle', subject: 'an eagle diving down at an angle, wings swept back and talons stretched forward'},
    ],
    styles: [
        {key: 'traditional', label: 'Traditional', style: 'American traditional tattoo flash, bold black outlines, heavy black shading and a limited flat palette', palette: 'red, yellow and green accents with black', framing: 'centered with a wide white margin'},
        {key: 'fineline', label: 'Fine line', style: 'a single-needle fine line tattoo drawing, one uniform hairline stroke, delicate detail and almost no shading', palette: 'black ink only', framing: 'centered with a wide white margin'},
        {key: 'dotwork', label: 'Dotwork', style: 'a dotwork tattoo drawing, a thin outline with all shading built from stippled dots and dot gradients', palette: 'black ink only', framing: 'centered with a wide white margin, the dots reading cleanly'},
    ],
    directions: ['a falcon', 'red ink', 'forearm'],
    directionHint: 'try: a falcon',
    seo: {
        title: 'Eagle tattoo designs: wings spread, eagle head, diving eagle',
        metaDescription: "Nine eagle tattoo designs: an eagle with wings spread, an eagle's head in profile and a diving eagle, in three styles.",
        h1: 'Eagle tattoo designs: wings spread, eagle head, diving eagle',
        tagline: 'Three eagles, each drawn in traditional flash, fine line and dotwork on white paper.',
        intro: "Eagle tattoo designs, worn for freedom and pride, on the chest, across the back or on the forearm. The nine sheets show an eagle facing forward with both wings spread, an eagle's head in profile with its hooked beak, and an eagle diving with its wings swept back. Each is drawn as traditional flash, fine line and dotwork. Type a different bird or twist, such as a falcon, and the same styles can be asked for.",
        useCases: [
            "a chest piece with an eagle's wings spread",
            "a forearm design with an eagle's head",
            'a back piece with a diving eagle',
            'a printed sheet to show an artist',
        ],
        faq: [
            {q: 'What is on the page?', a: "Nine eagle designs: an eagle with wings spread, an eagle's head and a diving eagle, each in three styles."},
            {q: 'Is this a design on skin?', a: 'No. Each design is a drawing on white paper, for taking to a studio or printing.'},
            {q: 'Can I ask for a falcon or red ink?', a: 'Yes. Type a different bird or twist, such as a falcon or red ink; the result is a new drawing, not a guaranteed match.'},
            {q: 'What is the difference between the styles?', a: 'Traditional flash has bold outlines and a few accent colours. Fine line is one uniform hairline with little shading. Dotwork builds the shadows from dots.'},
        ],
    },
}
